/*
 * Copyright 2018-2025 CommScope, Inc., All rights reserved.
 *
 * This program is confidential and proprietary to CommScope, Inc. (CommScope), and
 * may not be copied, reproduced, modified, disclosed to others, published or used, in
 * whole or in part, without the express prior written permission of CommScope.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { includes, isArray, isEmpty, toLower } from 'lodash';
import { Parser } from 'json2csv';
import Papa from 'papaparse';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core';
import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import {
  fetchSMBProWifiReport,
  smbProWifiReportFetchStateSelector,
  smbProWifiReportSelector
} from 'app/redux/reports';

import { StatefulTable } from 'app/components/tables';
import { NoDataToShow } from 'app/components/elements';
import { FailedFetchStateHandler } from 'app/components/utility';
import {
  ExportCsvIcon,
  FaIcon,
  StyledExportButton,
  LoadingIconPlaceholder
} from 'app/components/icons';
import {
  defaultCursorTheme,
  generateSMBColumn,
  reportsOptions
} from '../utils';
import { generateDynamicMonthColumns } from '../get-dynamic-month';

class SMBReportTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      searchValue: '',
      parsedData: [],
      isParsing: false
    };
  }

  componentDidMount = () => {
    const {
      smbProWifiReportFetchState: { inProgress, failed },
      smbProWifiReportData
    } = this.props;
    if (!inProgress && !failed && smbProWifiReportData instanceof Blob) {
      this.convertCSVToJSON(smbProWifiReportData);
    }
  };

  componentDidUpdate = prevProps => {
    const {
      smbProWifiReportFetchState: { inProgress: prevInProgress }
    } = prevProps;
    const {
      smbProWifiReportFetchState: { inProgress, failed },
      smbProWifiReportData
    } = this.props;

    if (
      prevInProgress &&
      !inProgress &&
      !failed &&
      smbProWifiReportData instanceof Blob
    ) {
      this.convertCSVToJSON(smbProWifiReportData);
    }
  };

  convertCSVToJSON = data => {
    this.setState({ isParsing: true });
    const file = new File([data], 'SMBProWifiReport.csv');
    Papa.parse(file, {
      header: true,
      worker: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: results =>
        this.setState({ isParsing: false, parsedData: results.data }),
      error: () => this.setState({ isParsing: false, parsedData: [] })
    });
  };

  handleCSVExport = () => {
    const { parsedData } = this.state;
    const customHeader = generateSMBColumn.map(c => ({
      label: c.label,
      value: row => row[c.name]
    }));
    const parser = new Parser({ fields: customHeader });
    const csv = parser.parse(parsedData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'SMBProWifi-Report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderCustomToolbar = () => (
    <StyledExportButton title="Generate CSV" onClick={this.handleCSVExport}>
      <ExportCsvIcon />
    </StyledExportButton>
  );

  filterBySearch = data => {
    const { searchValue } = this.state;

    if (isEmpty(searchValue)) {
      return data;
    }
    const lowerSearchValue = toLower(searchValue);

    return data.filter(row =>
      Object.values(row).some(value =>
        includes(toLower(String(value)), lowerSearchValue)
      )
    );
  };

  renderCustomSearch = () => {
    const { searchValue } = this.state;

    return (
      <div className="d-flex justify-content-between align-items-center m-2">
        <div className="d-flex align-items-center">
          <FaIcon icon="search" classes={['mr-2']} />
          <label className="sr-only">Search...</label>
          <input
            className="p-1"
            id="reports-smb-data-search"
            onChange={e => this.setState({ searchValue: e.target.value })}
            placeholder="Search..."
            style={{ width: 250 }}
            type="text"
            value={searchValue}
          />
        </div>
      </div>
    );
  };

  render = () => {
    const { parsedData, isParsing } = this.state;

    const {
      smbProWifiReportData,
      fetchSMBProWifiReport,
      smbProWifiReportFetchState: { inProgress }
    } = this.props;

    const hasData =
      smbProWifiReportData instanceof Blob &&
      isArray(parsedData) &&
      !isEmpty(parsedData);
    const noDataToShow = !inProgress && !isParsing && !hasData;

    const tableOptions = {
      ...reportsOptions,
      customToolbar: this.renderCustomToolbar,
      customSearchRender: this.renderCustomSearch
    };

    const allKeys =
      parsedData && parsedData.length > 0 ? Object.keys(parsedData[0]) : [];
    const dynamicMonthCols = generateDynamicMonthColumns(allKeys);

    // Ensure no duplicate column keys
    const combinedColumns = [
      ...generateSMBColumn.filter(
        col => !dynamicMonthCols.find(dynamic => dynamic.name === col.name)
      ),
      ...dynamicMonthCols
    ];

    return (
      <Fragment>
        <FailedFetchStateHandler
          fetchState={{ pending: inProgress, complete: !inProgress }}
          retry={fetchSMBProWifiReport}
        >
          {inProgress || isParsing ? (
            <LoadingIconPlaceholder position="relative" />
          ) : noDataToShow ? (
            <NoDataToShow
              message="No data available"
              style={{ background: 'none', position: 'relative' }}
            />
          ) : (
            hasData && (
              <Fragment>
                <MuiThemeProvider theme={createMuiTheme(defaultCursorTheme)}>
                  <StatefulTable
                    tableKey="smb-report"
                    data={this.filterBySearch(parsedData)}
                    columns={combinedColumns}
                    options={tableOptions}
                  />
                </MuiThemeProvider>
              </Fragment>
            )
          )}
        </FailedFetchStateHandler>
      </Fragment>
    );
  };
}

SMBReportTable.propTypes = {
  smbProWifiReportData: PropTypes.object,
  smbProWifiReportFetchState: PropTypes.object,
  fetchSMBProWifiReport: PropTypes.func
};

const mapStateToProps = createSelector(
  smbProWifiReportFetchStateSelector,
  smbProWifiReportSelector,
  (smbProWifiReportFetchState, smbProWifiReportData) => ({
    smbProWifiReportFetchState,
    smbProWifiReportData
  })
);

export default connect(mapStateToProps, { fetchSMBProWifiReport })(
  SMBReportTable
);
